import { INumberedDocumentView, WorkflowStatusType } from '../api-client/autogenerated';

/**
 * A submittal requirement is a specification section and a paragraph within it. Everything that
 * shares those two is the same requirement met again -- a later subparagraph, or a revision of one.
 *
 * The flows that build on an existing submittal (uploading against one, gathering them into a
 * package) are choosing a requirement, not a particular attempt at it, so they offer the latest of
 * each rather than every version the project has accumulated.
 */
export const submittalRequirementKey = (submittal: INumberedDocumentView) =>
  `${submittal.submittalSection ?? ''}|${submittal.submittalParagraphNumber ?? ''}`;

const asNumber = (value?: string | number | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Later within a requirement: the furthest subparagraph, then its highest revision. */
const isMoreRecent = (candidate: INumberedDocumentView, held: INumberedDocumentView) => {
  const bySubparagraph =
    asNumber(candidate.submittalSubparagraphNumber) - asNumber(held.submittalSubparagraphNumber);
  if (bySubparagraph !== 0) return bySubparagraph > 0;
  const byRevision = asNumber(candidate.revisionNumber) - asNumber(held.revisionNumber);
  if (byRevision !== 0) return byRevision > 0;
  return (candidate.createdOn ?? '') > (held.createdOn ?? '');
};

/**
 * One submittal per requirement -- the most recent -- keeping the order the submittals arrived in
 * so the caller's own sort still holds.
 */
export const latestSubmittalPerRequirement = (submittals: INumberedDocumentView[]) => {
  const latest = new Map<string, INumberedDocumentView>();
  submittals.forEach((submittal) => {
    const key = submittalRequirementKey(submittal);
    const held = latest.get(key);
    if (!held || isMoreRecent(submittal, held)) latest.set(key, submittal);
  });
  return submittals.filter(
    (submittal) => latest.get(submittalRequirementKey(submittal)) === submittal,
  );
};

/**
 * The statuses DocumentIndexUtils.statusToGeneralString maps to "Awaiting Submission" -- copied
 * rather than imported, since that module pulls in the whole document log page for one string
 * lookup. Keep in sync with statusToGeneralString if that mapping changes.
 */
const AWAITING_SUBMISSION_STATUSES: WorkflowStatusType[] = [
  WorkflowStatusType.Initial,
  WorkflowStatusType.SubcontractorUploaded,
  WorkflowStatusType.ReadyForSubmissionToArchitect,
];

/**
 * Whether uploading a file against this submittal should start a fresh one for the requirement
 * instead of continuing it -- true once the requirement has moved past being awaited (submitted,
 * under review, completed, ...), the same case "Create a New Submittal For This Requirement"
 * exists to handle.
 */
export const submittalRequirementNeedsFreshCopy = (submittal: INumberedDocumentView) =>
  !!submittal.workflowStatus && !AWAITING_SUBMISSION_STATUSES.includes(submittal.workflowStatus);

/**
 * The number a new submittal against `source`'s requirement takes, mirroring
 * AddDocumentDialog's getNextSubmittalParagraphNumberForRequirement -- which is what the
 * "Create a New Submittal For This Requirement" button on a submittal's page uses.
 *
 * A submittal that already carries a subparagraph keeps its paragraph and takes the next
 * subparagraph; one that does not takes the next paragraph among the section's other
 * subparagraph-less submittals.
 */
export const getNextNumberInRequirement = (
  source: INumberedDocumentView,
  allSubmittals: INumberedDocumentView[],
) => {
  const hasSubparagraph = !!source.submittalSubparagraphNumber;
  const withinRequirement = allSubmittals.filter(
    (submittal) =>
      submittal.submittalSection === source.submittalSection &&
      (hasSubparagraph
        ? submittal.submittalParagraphNumber === source.submittalParagraphNumber
        : !submittal.submittalSubparagraphNumber),
  );
  const highest = Math.max(
    0,
    ...withinRequirement.map((submittal) =>
      asNumber(
        hasSubparagraph
          ? submittal.submittalSubparagraphNumber
          : submittal.submittalParagraphNumber,
      ),
    ),
  );
  const next = (highest + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 });
  return {
    submittalParagraphNumber: hasSubparagraph ? source.submittalParagraphNumber || undefined : next,
    submittalSubparagraphNumber: hasSubparagraph ? next : undefined,
  };
};
